"use client"

import { useState, useRef, useEffect } from "react"
import { createPortal } from "react-dom"
// @ts-ignore — "three" não tem @types instalado nesse projeto; os outros
// arquivos que usam three.js são .js (sem checagem de tipo), esse é o
// primeiro .tsx a importar direto. Rode "npm i --save-dev @types/three"
// se preferir a correção "oficial" em vez desse ignore.
import * as THREE from "three"
import jsPDF from "jspdf"
import { saveApproval, getClientPublicIP } from "../../../API"

const DEFAULT_APPROVAL_MESSAGE = `TERMO DE ACEITAÇÃO - CONFIRMAÇÃO DE UNIFORME

Prezado(a) Cliente,

Para garantir a qualidade e a precisão na produção do seu uniforme, solicitamos que verifique os seguintes pontos antes da aprovação final:

Cores: Lembre-se que as cores exibidas no seu dispositivo podem apresentar variações devido à diferença nos tons RGB de cada tela. Recomendamos que você confira o modelo em um ambiente com boa iluminação.

Nomes e Números: Certifique-se de que todos os nomes e números estejam corretos, conforme solicitado, e que o design esteja conforme o aprovado.

Qualquer alteração após a confirmação pode gerar custos adicionais e atrasos. Por favor, revise todos os detalhes com atenção.

Se estiver tudo correto, confirme sua aprovação assinando abaixo, para que possamos dar continuidade à produção do seu uniforme.

Em caso de dúvidas ou ajustes, entre em contato conosco o quanto antes.

Atenciosamente,`

function detectDeviceInfo(): string {
  if (typeof navigator === "undefined") return ""
  const ua = navigator.userAgent

  let browser = "Navegador desconhecido"
  const browserMatch = ua.match(/(Chrome|Firefox|Safari|Edg)\/([\d.]+)/)
  if (browserMatch) {
    const name = browserMatch[1] === "Edg" ? "Edge" : browserMatch[1]
    browser = `${name} v${browserMatch[2]}`
  }

  let os = "Sistema desconhecido"
  if (ua.includes("Windows")) os = "Windows"
  else if (ua.includes("Mac OS")) os = "macOS"
  else if (ua.includes("Android")) os = "Android"
  else if (ua.includes("iPhone") || ua.includes("iPad")) os = "iOS"
  else if (ua.includes("Linux")) os = "Linux"

  return `${browser} no ${os}`
}

// Tira um print bem enquadrado do modelo 3D — calcula a bounding box e
// redimensiona o canvas temporariamente pra retrato, sem área morta nas
// laterais. Mesma técnica usada nos outros PDFs do sistema (Sections.jsx).
async function captureTightShot({ rotateY = 0 }: { rotateY?: number } = {}): Promise<string | null> {
  if (typeof window === "undefined") return null
  const model = (window as any).model
  const renderer = (window as any).renderer
  const camera = (window as any).camera
  const scene = (window as any).scene
  const controls = (window as any).controls
  if (!model || !renderer || !camera || !scene) return null

  const originalRotationY = model.rotation.y
  const originalWidth = renderer.domElement.width
  const originalHeight = renderer.domElement.height
  const originalAspect = camera.aspect
  const originalPixelRatio = renderer.getPixelRatio ? renderer.getPixelRatio() : 1
  const originalCameraPos = camera.position.clone()
  const originalTarget = controls ? controls.target.clone() : null

  try {
    model.rotation.y = originalRotationY + rotateY
    model.updateWorldMatrix(true, true)

    const box = new THREE.Box3().setFromObject(model)
    const size = box.getSize(new THREE.Vector3())
    const center = box.getCenter(new THREE.Vector3())

    const targetW = 900, targetH = 1200
    renderer.setPixelRatio(1)
    renderer.setSize(targetW, targetH, false)
    camera.aspect = targetW / targetH

    const fov = camera.fov * (Math.PI / 180)
    const distanceForHeight = (size.y / 2) / Math.tan(fov / 2)
    const distanceForWidth = (size.x / 2) / Math.tan((fov * camera.aspect) / 2)
    const distance = Math.max(distanceForHeight, distanceForWidth) * 1.15

    camera.position.set(center.x, center.y, center.z + distance)
    camera.lookAt(center)
    camera.updateProjectionMatrix()

    renderer.render(scene, camera)
    await new Promise((resolve) => setTimeout(resolve, 50))
    renderer.render(scene, camera)

    return renderer.domElement.toDataURL("image/png")
  } catch (e) {
    console.warn("[captureTightShot] erro ao capturar imagem enquadrada:", e)
    return null
  } finally {
    model.rotation.y = originalRotationY
    renderer.setPixelRatio(originalPixelRatio)
    renderer.setSize(originalWidth, originalHeight, false)
    camera.aspect = originalAspect
    camera.position.copy(originalCameraPos)
    if (controls && originalTarget) controls.target.copy(originalTarget)
    camera.updateProjectionMatrix()
    if (controls) controls.update()
    renderer.render(scene, camera)
  }
}

// ── Assinatura desenhada (canvas simples, mouse + touch) ────────────────────
function SignaturePad({ onChange }: { onChange: (dataUrl: string | null) => void }) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const drawingRef = useRef(false)
  const hasDrawnRef = useRef(false)

  useEffect(() => {
    const canvas = canvasRef.current
    const ctx = canvas?.getContext("2d")
    if (!ctx) return
    ctx.lineWidth = 2.5
    ctx.lineCap = "round"
    ctx.strokeStyle = "#111"
  }, [])

  const getPos = (e: React.MouseEvent | React.TouchEvent) => {
    const canvas = canvasRef.current!
    const rect = canvas.getBoundingClientRect()
    const point = "touches" in e ? e.touches[0] : (e as React.MouseEvent)
    return {
      x: (point.clientX - rect.left) * (canvas.width / rect.width),
      y: (point.clientY - rect.top) * (canvas.height / rect.height),
    }
  }

  const start = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault()
    drawingRef.current = true
    const ctx = canvasRef.current?.getContext("2d")
    if (!ctx) return
    const { x, y } = getPos(e)
    ctx.beginPath()
    ctx.moveTo(x, y)
  }

  const move = (e: React.MouseEvent | React.TouchEvent) => {
    if (!drawingRef.current) return
    e.preventDefault()
    const ctx = canvasRef.current?.getContext("2d")
    if (!ctx) return
    const { x, y } = getPos(e)
    ctx.lineTo(x, y)
    ctx.stroke()
    hasDrawnRef.current = true
  }

  const end = () => {
    if (!drawingRef.current) return
    drawingRef.current = false
    onChange(hasDrawnRef.current ? canvasRef.current?.toDataURL("image/png") ?? null : null)
  }

  const clear = () => {
    const canvas = canvasRef.current
    const ctx = canvas?.getContext("2d")
    if (!canvas || !ctx) return
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    hasDrawnRef.current = false
    onChange(null)
  }

  return (
    <div>
      <canvas
        ref={canvasRef}
        width={500}
        height={160}
        style={{ width: "100%", height: 160, background: "#fff", borderRadius: 12, touchAction: "none", cursor: "crosshair", display: "block" }}
        onMouseDown={start}
        onMouseMove={move}
        onMouseUp={end}
        onMouseLeave={end}
        onTouchStart={start}
        onTouchMove={move}
        onTouchEnd={end}
      />
      <button onClick={clear} style={{ fontSize: 12, color: "#888", marginTop: 6 }}>
        Limpar assinatura
      </button>
    </div>
  )
}

// ── Fluxo completo: termo + lista de pedidos + assinatura → comprovante ─────
export function ApprovalFlow({
  ownerId,
  iframeOptionsId,
  projectId,
  companyName,
  companyEmail,
  clientName,
  onClose,
}: {
  ownerId: string
  iframeOptionsId?: number | string
  projectId?: string
  companyName?: string
  companyEmail?: string
  clientName: string
  onClose: () => void
}) {
  const [step, setStep] = useState<"terms" | "receipt">("terms")
  const [signatureDataUrl, setSignatureDataUrl] = useState<string | null>(null)
  const [agreed, setAgreed] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [receipt, setReceipt] = useState<any>(null)
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false)

  const handleApprove = async () => {
    if (!agreed) {
      setError("Você precisa concordar com o modelo apresentado para prosseguir.")
      return
    }
    if (!signatureDataUrl) {
      setError("Desenhe sua assinatura antes de confirmar.")
      return
    }
    setIsSubmitting(true)
    setError(null)
    try {
      const ip = await getClientPublicIP()
      const deviceInfo = detectDeviceInfo()
      
      // Captura thumbnail do modelo 3D para o pedido
      const thumbDataUrl = await captureTightShot({ rotateY: 0 })

      const saved = await saveApproval({
        ownerId,
        iframeOptionsId,
        projectId,
        clientName,
        message: DEFAULT_APPROVAL_MESSAGE,
        signatureDataUrl,
        orderList: null,
        ipAddress: ip,
        deviceInfo,
        thumbDataUrl,
      })
      if (!saved) throw new Error("Não foi possível registrar a aprovação. Tente novamente.")
      setReceipt({ ...saved, ip_address: ip, device_info: deviceInfo })
      setStep("receipt")
    } catch (err: any) {
      setError(err?.message || "Erro ao registrar a aprovação.")
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDownloadPdf = async () => {
    if (!receipt) return
    setIsDownloadingPdf(true)
    try {
      const doc = new jsPDF({ unit: "pt", format: "a4" })
      const pageWidth = doc.internal.pageSize.getWidth()
      const margin = 40
      let y = margin

      doc.setFont("helvetica", "bold")
      doc.setFontSize(22)
      doc.setTextColor("#111111")
      doc.text("Comprovante de Aceite", margin, y + 10)
      y += 34
      doc.setDrawColor("#e5e5e5")
      doc.setLineWidth(1)
      doc.line(margin, y, pageWidth - margin, y)
      y += 26

      doc.setFont("helvetica", "bold")
      doc.setFontSize(14)
      doc.setTextColor("#2563eb")
      doc.text(companyName || "Loja", margin, y)
      if (companyEmail) {
        doc.setFont("helvetica", "normal")
        doc.setFontSize(10)
        doc.setTextColor("#666666")
        doc.text(companyEmail, margin, y + 14)
      }

      doc.setFillColor("#16a34a")
      const badgeText = "Aprovado para Produção"
      doc.setFont("helvetica", "bold")
      doc.setFontSize(9)
      const badgeW = doc.getTextWidth(badgeText) + 20
      doc.roundedRect(pageWidth - margin - badgeW, y - 12, badgeW, 20, 10, 10, "F")
      doc.setTextColor("#ffffff")
      doc.text(badgeText, pageWidth - margin - badgeW / 2, y + 2, { align: "center" })

      doc.setFont("helvetica", "normal")
      doc.setFontSize(9)
      doc.setTextColor("#999999")
      doc.text(
        new Date(receipt.created_at || Date.now()).toLocaleString("pt-BR"),
        pageWidth - margin,
        y + 22,
        { align: "right" }
      )
      y += 40

      const liveSceneAvailable = typeof window !== "undefined" && (window as any).model && (window as any).renderer && (window as any).camera
      if (liveSceneAvailable) {
        const boxW = 160, boxH = 200, gap = 16
        const frontData = await captureTightShot({ rotateY: 0 })
        const backData = await captureTightShot({ rotateY: Math.PI })

        const totalWidth = backData ? boxW * 2 + gap : boxW
        const startX = (pageWidth - totalWidth) / 2

        const drawBoxedImage = (imgData: string, x: number) => {
          try {
            const props = (doc as any).getImageProperties(imgData)
            const ratio = Math.min(boxW / props.width, boxH / props.height)
            const w = props.width * ratio, h = props.height * ratio
            doc.addImage(imgData, "PNG", x + (boxW - w) / 2, y + (boxH - h) / 2, w, h)
          } catch (e) {
            console.warn("[ApprovalFlow] falha ao inserir imagem no PDF:", e)
          }
        }

        if (frontData) {
          drawBoxedImage(frontData, startX)
          doc.setFont("helvetica", "bold")
          doc.setFontSize(8)
          doc.setTextColor("#999999")
          doc.text("FRENTE", startX + boxW / 2, y + boxH + 14, { align: "center" })
        }
        if (backData) {
          drawBoxedImage(backData, startX + boxW + gap)
          doc.setFont("helvetica", "bold")
          doc.setFontSize(8)
          doc.setTextColor("#999999")
          doc.text("COSTAS", startX + boxW + gap + boxW / 2, y + boxH + 14, { align: "center" })
        }
        y += boxH + 34
      }

      const sectionLabel = (label: string) => {
        doc.setFont("helvetica", "bold")
        doc.setFontSize(10)
        doc.setTextColor("#999999")
        doc.text(label.toUpperCase(), margin, y)
        y += 6
        doc.setDrawColor("#eeeeee")
        doc.line(margin, y, pageWidth - margin, y)
        y += 16
      }

      sectionLabel("Mensagem de Aceite")
      doc.setFont("helvetica", "italic")
      doc.setFontSize(9.5)
      doc.setTextColor("#444444")
      const messageLines = doc.splitTextToSize(receipt.message, pageWidth - margin * 2 - 10)
      doc.text(messageLines, margin + 10, y)
      y += messageLines.length * 12 + 20

      if (receipt.order_list) {
        sectionLabel("Lista de Pedidos")
        doc.setFont("helvetica", "normal")
        doc.setFontSize(10)
        doc.setTextColor("#333333")
        const orderLines = doc.splitTextToSize(receipt.order_list, pageWidth - margin * 2)
        doc.text(orderLines, margin, y)
        y += orderLines.length * 13 + 20
      }

      sectionLabel("Dados da Assinatura")
      doc.setFont("helvetica", "normal")
      doc.setFontSize(10)
      doc.setTextColor("#333333")
      const infoLines = [
        `Assinado por: ${receipt.client_name}`,
        receipt.ip_address ? `IP de Registro: ${receipt.ip_address}` : null,
        receipt.device_info ? `Dispositivo: ${receipt.device_info}` : null,
      ].filter(Boolean) as string[]
      infoLines.forEach((line, i) => doc.text(line, margin, y + i * 16))

      if (receipt.signature_data_url) {
        try {
          const props = (doc as any).getImageProperties(receipt.signature_data_url)
          const sigW = 130
          const sigH = (props.height / props.width) * sigW
          doc.addImage(receipt.signature_data_url, "PNG", pageWidth - margin - sigW, y - 10, sigW, sigH)
          doc.setDrawColor("#cccccc")
          doc.line(pageWidth - margin - sigW, y + sigH - 2, pageWidth - margin, y + sigH - 2)
          doc.setFont("helvetica", "normal")
          doc.setFontSize(8)
          doc.setTextColor("#999999")
          doc.text("Assinatura digital", pageWidth - margin - sigW / 2, y + sigH + 8, { align: "center" })
        } catch (e) {
          console.warn("[ApprovalFlow] falha ao inserir assinatura no PDF:", e)
        }
      }

      doc.save(`comprovante-aceite-${receipt.client_name?.replace(/\s+/g, "-") || "cliente"}.pdf`)
    } finally {
      setIsDownloadingPdf(false)
    }
  }

  if (typeof document === "undefined") return null

  return createPortal(
    <div style={{ position: "fixed", inset: 0, zIndex: 2147483647, background: "rgba(0,0,0,.7)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <style>{"@media print { .ks-approval-no-print { display: none !important; } }"}</style>

      <div style={{ width: "100%", maxWidth: 560, maxHeight: "92vh", overflowY: "auto", background: "#fff", borderRadius: 16, boxShadow: "0 20px 50px rgba(0,0,0,.4)" }}>

        {step === "terms" && (
          <div style={{ padding: 24 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <h2 style={{ fontSize: 18, fontWeight: 700, color: "#111" }}>Aprovar Arte</h2>
              <button onClick={onClose} style={{ color: "#888", fontSize: 18 }}>✕</button>
            </div>

            <div style={{ fontSize: 12.5, color: "#333", whiteSpace: "pre-line", lineHeight: 1.6, maxHeight: 200, overflowY: "auto", border: "1px solid #eee", borderRadius: 12, padding: 14, background: "#fafafa" }}>
              {DEFAULT_APPROVAL_MESSAGE}
            </div>

            <div style={{ marginTop: 16, display: "flex", alignItems: "flex-start", gap: 10 }}>
              <input
                type="checkbox"
                id="agree-checkbox"
                checked={agreed}
                onChange={(e) => { setAgreed(e.target.checked); setError(null); }}
                style={{ marginTop: 2, width: 16, height: 16, cursor: "pointer", accentColor: "#16a34a" }}
              />
              <label htmlFor="agree-checkbox" style={{ fontSize: 13, color: "#333", cursor: "pointer", lineHeight: 1.5 }}>
                Conferi o layout e concordo com o modelo apresentado.
              </label>
            </div>

            <div style={{ marginTop: 16 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#555" }}>Assine abaixo pra confirmar:</label>
              <div style={{ marginTop: 6, border: "1px solid #ddd", borderRadius: 12, padding: 4 }}>
                <SignaturePad onChange={setSignatureDataUrl} />
              </div>
            </div>

            {error && <p style={{ color: "#dc2626", fontSize: 12, marginTop: 10 }}>{error}</p>}

            <button
              onClick={handleApprove}
              disabled={isSubmitting || !agreed}
              style={{ marginTop: 18, width: "100%", background: "#16a34a", color: "#fff", fontWeight: 700, fontSize: 14, padding: "12px 0", borderRadius: 12, opacity: isSubmitting || !agreed ? 0.5 : 1, cursor: isSubmitting || !agreed ? "not-allowed" : "pointer" }}
            >
              {isSubmitting ? "Enviando..." : "✓ Confirmar Aprovação"}
            </button>
          </div>
        )}

        {step === "receipt" && receipt && (
          <div>
            <div style={{ padding: "28px 28px 20px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <h1 style={{ fontSize: 24, fontWeight: 600, color: "#1a1a1a", margin: 0 }}>Comprovante de Aceite</h1>
                <div className="ks-approval-no-print" style={{ display: "flex", gap: 8 }}>
                  <button
                    onClick={handleDownloadPdf}
                    disabled={isDownloadingPdf}
                    style={{ display: "flex", alignItems: "center", gap: 6, background: "#2563eb", color: "#fff", fontSize: 12, fontWeight: 600, padding: "9px 14px", borderRadius: 8, whiteSpace: "nowrap", opacity: isDownloadingPdf ? 0.6 : 1 }}
                  >
                    {isDownloadingPdf ? "Gerando…" : "⬇ Baixar PDF"}
                  </button>
                  <button
                    onClick={() => window.print()}
                    style={{ display: "flex", alignItems: "center", gap: 6, background: "#f3f4f6", color: "#333", fontSize: 12, fontWeight: 600, padding: "9px 14px", borderRadius: 8, whiteSpace: "nowrap" }}
                  >
                    🖨️ Imprimir
                  </button>
                </div>
              </div>

              <hr style={{ margin: "18px 0", border: "none", borderTop: "1px solid #eee" }} />

              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
                <div>
                  <div style={{ fontSize: 17, fontWeight: 700, color: "#2563eb" }}>{companyName || "Loja"}</div>
                  {companyEmail && <div style={{ fontSize: 12, color: "#888" }}>{companyEmail}</div>}
                </div>
                <div style={{ textAlign: "right", flexShrink: 0 }}>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "#16a34a", color: "#fff", fontSize: 11, fontWeight: 700, padding: "5px 12px", borderRadius: 999, whiteSpace: "nowrap" }}>
                    ✓ Aprovado para Produção
                  </span>
                  <div style={{ fontSize: 11, color: "#999", marginTop: 5 }}>
                    {new Date(receipt.created_at || Date.now()).toLocaleString("pt-BR")}
                  </div>
                </div>
              </div>

              <div style={{ marginTop: 24, fontSize: 12, fontWeight: 700, color: "#888" }}>Mensagem de Aceite</div>
              <hr style={{ margin: "8px 0 12px", border: "none", borderTop: "1px solid #eee" }} />
              <div style={{ borderLeft: "3px solid #2563eb", paddingLeft: 14, fontSize: 12.5, color: "#444", fontStyle: "italic", whiteSpace: "pre-line", lineHeight: 1.7 }}>
                {receipt.message}
              </div>

              {receipt.order_list && (
                <>
                  <div style={{ marginTop: 24, fontSize: 12, fontWeight: 700, color: "#888" }}>Lista de Pedidos</div>
                  <hr style={{ margin: "8px 0 12px", border: "none", borderTop: "1px solid #eee" }} />
                  <div style={{ fontSize: 12.5, color: "#333", whiteSpace: "pre-line", lineHeight: 1.6 }}>
                    {receipt.order_list}
                  </div>
                </>
              )}

              <div style={{ marginTop: 24, fontSize: 12, fontWeight: 700, color: "#888" }}>Dados da Assinatura</div>
              <hr style={{ margin: "8px 0 12px", border: "none", borderTop: "1px solid #eee" }} />
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16 }}>
                <div style={{ fontSize: 12.5, color: "#333", lineHeight: 1.9 }}>
                  <div><b>Assinado por:</b> {receipt.client_name}</div>
                  {receipt.ip_address && <div><b>IP de Registro:</b> {receipt.ip_address}</div>}
                  {receipt.device_info && <div><b>Dispositivo:</b> {receipt.device_info}</div>}
                </div>
                <div style={{ textAlign: "center", flexShrink: 0 }}>
                  <img src={receipt.signature_data_url} alt="Assinatura" style={{ maxHeight: 70, maxWidth: 160 }} />
                  <div style={{ fontSize: 10, color: "#999", borderTop: "1px solid #ccc", marginTop: 2, paddingTop: 2 }}>Assinatura digital</div>
                </div>
              </div>
            </div>

            <div style={{ background: "#f7f7f8", padding: "14px 28px", fontSize: 10.5, color: "#999", textAlign: "center", borderTop: "1px solid #eee" }}>
              Este é um documento eletrônico gerado pelo sistema. A autenticidade deste aceite é garantida pelo registro de IP e timestamp.
            </div>

            <div style={{ padding: "16px 28px 24px" }} className="ks-approval-no-print">
              <button onClick={onClose} style={{ width: "100%", background: "#f3f4f6", color: "#333", fontWeight: 600, fontSize: 13, padding: "10px 0", borderRadius: 10 }}>
                Fechar
              </button>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  )
}